<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { ApiError, presentationsApi } from '../api'
import AppShell from '../components/AppShell.vue'
import ErrorState from '../components/ErrorState.vue'
import LoadingState from '../components/LoadingState.vue'
import SlideForm from '../components/SlideForm.vue'
import SlidePreview from '../components/SlidePreview.vue'
import ThemeGallery from '../components/ThemeGallery.vue'
import { navigate } from '../router'
import { themeName } from '../themeData'
import { notify } from '../toasts'
import type {
  Aspect,
  LayoutId,
  PresentationDetails,
  PresetThemeId,
  Revision,
  Slide,
  SlidePatchOperation,
} from '../types'

const props = defineProps<{
  id: string
}>()

defineEmits<{
  toggleTheme: []
}>()

const details = ref<PresentationDetails | null>(null)
const loading = ref(true)
const error = ref('')
const busy = ref('')
const selectedIndex = ref(0)
const aiInstruction = ref('')
const settingsOpen = ref(false)
const themeChoice = ref<PresetThemeId | 'auto'>('clean')
const aspectChoice = ref<Aspect>('16:9')
const renderTick = ref(0)
const uploadInput = ref<HTMLInputElement | null>(null)
let pollTimer = 0
let pollAttempts = 0

const layoutOptions: Array<{ id: LayoutId; name: string; hint: string }> = [
  { id: 'title', name: 'Титульный', hint: 'Большое начало' },
  { id: 'section', name: 'Раздел', hint: 'Переход между темами' },
  { id: 'bullets', name: 'Тезисы', hint: 'От 2 до 6 пунктов' },
  { id: 'text', name: 'Текст', hint: 'Одна раскрытая мысль' },
  { id: 'quote', name: 'Цитата', hint: 'Сильная формулировка' },
  { id: 'stats', name: 'Цифры', hint: '2–4 показателя' },
  { id: 'two_cols', name: 'Две колонки', hint: 'Сравнение' },
  { id: 'steps', name: 'Шаги', hint: 'Последовательность' },
  { id: 'image', name: 'Изображение', hint: 'Картинка и подпись' },
  { id: 'closing', name: 'Финал', hint: 'Вывод и контакт' },
]

const selectedSlide = computed<Slide | null>(
  () => details.value?.revision.doc.slides[selectedIndex.value] ?? null,
)

const selectedLayout = computed({
  get: () => selectedSlide.value?.layout ?? 'title',
  set: (value: LayoutId) => {
    if (value !== selectedSlide.value?.layout) void changeLayout(value)
  },
})

const allSlidesRendered = computed(() => {
  if (!details.value) return false
  const pngs = details.value.renders.filter((render) => render.kind === 'png')
  const hasPdf = details.value.renders.some((render) => render.kind === 'pdf')
  return pngs.length >= details.value.revision.doc.slides.length && hasPdf
})

function previewUrl(index: number): string {
  if (!details.value) return ''
  const exists = details.value.renders.some(
    (render) => render.kind === 'png' && render.slideNumber === index + 1,
  )
  if (!exists) return ''
  return `/api/presentations/${encodeURIComponent(props.id)}/preview/${index + 1}?r=${details.value.revision.number}&t=${renderTick.value}`
}

async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true
  error.value = ''
  try {
    const response = await presentationsApi.get(props.id)
    details.value = response
    themeChoice.value = response.revision.doc.theme.id === 'custom'
      ? 'auto'
      : response.revision.doc.theme.id
    aspectChoice.value = response.revision.doc.aspect
    selectedIndex.value = Math.min(
      selectedIndex.value,
      response.revision.doc.slides.length - 1,
    )
    renderTick.value += 1
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось открыть презентацию.'
  } finally {
    loading.value = false
  }
}

function scheduleRenderPoll(): void {
  window.clearTimeout(pollTimer)
  pollAttempts = 0
  const poll = async () => {
    pollAttempts += 1
    await load(true)
    if (!allSlidesRendered.value && pollAttempts < 150) {
      pollTimer = window.setTimeout(poll, 2_000)
    }
  }
  pollTimer = window.setTimeout(poll, 1_200)
}

function applyRevision(revision: Revision, rendering = true): void {
  if (!details.value) return
  details.value.revision = revision
  details.value.renders = []
  details.value.presentation.title = revision.doc.title
  details.value.presentation.aspect = revision.doc.aspect
  details.value.presentation.themeId = revision.doc.theme.id
  renderTick.value += 1
  if (rendering) scheduleRenderPoll()
}

async function saveSlide(ops: SlidePatchOperation[]): Promise<void> {
  if (!details.value || !selectedSlide.value) return
  busy.value = 'save'
  try {
    const response = await presentationsApi.patchSlide(
      props.id,
      selectedSlide.value.id,
      details.value.revision.number,
      ops,
    )
    applyRevision(response.revision)
    notify('Слайд сохранён', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

async function aiEdit(): Promise<void> {
  if (!details.value || !selectedSlide.value || !aiInstruction.value.trim()) return
  busy.value = 'ai'
  try {
    const response = await presentationsApi.aiEditSlide(
      props.id,
      selectedSlide.value.id,
      details.value.revision.number,
      aiInstruction.value.trim(),
    )
    applyRevision(response.revision)
    aiInstruction.value = ''
    notify('Слайд переделан — остальные не изменились', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

function slideHeadline(slide: Slide): string {
  for (const key of ['title', 'quote', 'body', 'caption']) {
    const value = slide.content[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return 'Новый слайд'
}

function defaultContent(layout: LayoutId, slide: Slide): Record<string, unknown> {
  const headline = slideHeadline(slide).slice(0, layout === 'section' ? 40 : 70)
  switch (layout) {
    case 'title': return { title: headline, subtitle: 'Короткое пояснение' }
    case 'section': return { title: headline }
    case 'bullets': return { title: headline, items: ['Первый тезис', 'Второй тезис'] }
    case 'text': return { title: headline, body: 'Раскройте здесь одну важную мысль.' }
    case 'quote': return { quote: headline.slice(0, 220) }
    case 'stats': return {
      title: headline,
      stats: [
        { value: '42%', label: 'первый показатель' },
        { value: '2×', label: 'второй показатель' },
      ],
    }
    case 'two_cols': return {
      title: headline,
      left: { title: 'Было', items: ['Первый пункт'] },
      right: { title: 'Стало', items: ['Первый пункт'] },
    }
    case 'steps': return {
      title: headline,
      steps: [{ title: 'Первый шаг' }, { title: 'Второй шаг' }],
    }
    case 'image': return { title: headline }
    case 'closing': return { title: headline, subtitle: 'Спасибо' }
  }
}

async function changeLayout(layout: LayoutId): Promise<void> {
  if (!details.value || !selectedSlide.value) return
  const current = selectedSlide.value
  busy.value = 'layout'
  try {
    const response = await presentationsApi.changeLayout(
      props.id,
      current.id,
      details.value.revision.number,
      layout,
      defaultContent(layout, current),
    )
    applyRevision(response.revision)
    notify('Макет изменён. Текст можно уточнить ниже.', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

async function uploadImage(event: Event): Promise<void> {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  target.value = ''
  if (!file || !details.value || !selectedSlide.value) return
  busy.value = 'image'
  try {
    const response = await presentationsApi.uploadImage(
      props.id,
      selectedSlide.value.id,
      details.value.revision.number,
      file,
      file.name.replace(/\.[^.]+$/, ''),
    )
    applyRevision(response.revision)
    notify('Изображение загружено', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

async function changeTheme(): Promise<void> {
  if (!details.value) return
  if (!window.confirm('Перерисовать все слайды в новой теме? Текст останется прежним.')) return
  busy.value = 'theme'
  try {
    const response = await presentationsApi.changeTheme(
      props.id,
      details.value.revision.number,
      themeChoice.value === 'auto'
        ? { id: 'auto', description: details.value.revision.doc.title }
        : { id: themeChoice.value },
    )
    applyRevision(response.revision)
    notify('Тема меняется на всех слайдах', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

async function changeAspect(): Promise<void> {
  if (!details.value || aspectChoice.value === details.value.revision.doc.aspect) return
  if (!window.confirm(`Перерисовать всю презентацию в формате ${aspectChoice.value}?`)) {
    aspectChoice.value = details.value.revision.doc.aspect
    return
  }
  busy.value = 'aspect'
  try {
    const response = await presentationsApi.changeAspect(
      props.id,
      details.value.revision.number,
      aspectChoice.value,
    )
    applyRevision(response.revision)
    notify('Формат изменён', 'success')
  } catch (cause) {
    aspectChoice.value = details.value.revision.doc.aspect
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

async function publish(): Promise<string | null> {
  if (!details.value) return null
  busy.value = 'publish'
  try {
    const response = await presentationsApi.publish(props.id)
    details.value.publicUrl = response.publicUrl
    details.value.presentation.publishedRevisionId = response.revision.id
    notify('Опубликовано. Ссылка уже работает.', 'success')
    return response.publicUrl
  } catch (cause) {
    handleMutationError(cause)
    return null
  } finally {
    busy.value = ''
  }
}

async function show(): Promise<void> {
  if (!details.value) return
  if (
    !details.value.publicUrl
    || details.value.presentation.publishedRevisionId !== details.value.revision.id
  ) {
    const publicUrl = await publish()
    if (!publicUrl) return
  }
  navigate(`/p/${details.value.presentation.slug}/show`)
}

async function undo(): Promise<void> {
  if (!details.value) return
  busy.value = 'undo'
  try {
    const response = await presentationsApi.undo(props.id, details.value.revision.number)
    applyRevision(response.revision, false)
    await load(true)
    notify('Вернули предыдущую версию', 'success')
  } catch (cause) {
    handleMutationError(cause)
  } finally {
    busy.value = ''
  }
}

function handleMutationError(cause: unknown): void {
  if (cause instanceof ApiError && cause.status === 409) {
    notify('Презентация изменилась в другом окне. Обновляем свежую версию.', 'error')
    void load(true)
    return
  }
  notify(cause instanceof Error ? cause.message : 'Не удалось сохранить изменение.', 'error')
}

onMounted(async () => {
  await load()
  if (!allSlidesRendered.value) scheduleRenderPoll()
})

onBeforeUnmount(() => window.clearTimeout(pollTimer))
</script>

<template>
  <AppShell compact @toggle-theme="$emit('toggleTheme')">
    <LoadingState v-if="loading" title="Открываем редактор" full />
    <ErrorState v-else-if="error || !details" :text="error || 'Презентация не найдена.'" @retry="load()" />

    <div v-else class="editor">
      <header class="editor-bar">
        <div class="editor-title">
          <a href="/" class="icon-button" aria-label="Назад к презентациям">←</a>
          <div>
            <h1>{{ details.revision.doc.title }}</h1>
            <span>
              {{ details.revision.doc.slides.length }} слайдов ·
              {{ themeName(details.revision.doc.theme.id) }} ·
              ревизия {{ details.revision.number }}
            </span>
          </div>
        </div>
        <div class="editor-actions">
          <button type="button" class="button button--ghost" :disabled="Boolean(busy)" @click="undo">
            ↶ <span>Отменить</span>
          </button>
          <a
            :href="`/api/presentations/${id}/pdf`"
            target="_blank"
            class="button button--secondary"
            :class="{ 'is-disabled': !allSlidesRendered }"
            :aria-disabled="!allSlidesRendered"
            @click="!allSlidesRendered && $event.preventDefault()"
          >PDF</a>
          <button type="button" class="button button--secondary" :disabled="Boolean(busy) || !allSlidesRendered" @click="show">
            ▶ Показать
          </button>
          <button type="button" class="button button--primary" :disabled="Boolean(busy) || !allSlidesRendered" @click="publish">
            <span v-if="busy === 'publish'" class="mini-loader"></span>
            {{ details.publicUrl ? 'Обновить публикацию' : 'Опубликовать' }}
          </button>
        </div>
      </header>

      <div v-if="!allSlidesRendered" class="render-banner">
        <span class="mini-loader"></span>
        Дорисовываем превью. Редактировать уже можно.
      </div>

      <div class="editor-workspace">
        <aside class="slide-strip">
          <button
            v-for="(slide, index) in details.revision.doc.slides"
            :key="slide.id"
            type="button"
            class="slide-strip__item"
            :class="{ 'is-selected': selectedIndex === index }"
            @click="selectedIndex = index"
          >
            <span class="slide-number">{{ String(index + 1).padStart(2, '0') }}</span>
            <SlidePreview
              :src="previewUrl(index)"
              :slide="slide"
              :aspect="details.revision.doc.aspect"
              :alt="`Слайд ${index + 1}`"
            />
            <span
              v-if="details.revision.qualityIssues.some((issue) => issue.slideId === slide.id)"
              class="quality-dot"
              title="Этот слайд стоит проверить"
            >!</span>
          </button>
        </aside>

        <section class="editor-canvas">
          <div class="canvas-topline">
            <span>Слайд {{ selectedIndex + 1 }} из {{ details.revision.doc.slides.length }}</span>
            <button type="button" class="text-button" @click="settingsOpen = !settingsOpen">
              {{ settingsOpen ? 'Закрыть настройки' : 'Тема и формат' }} ⚙
            </button>
          </div>
          <div class="canvas-stage">
            <SlidePreview
              v-if="selectedSlide"
              :src="previewUrl(selectedIndex)"
              :slide="selectedSlide"
              :aspect="details.revision.doc.aspect"
              :alt="`Слайд ${selectedIndex + 1}`"
              eager
            />
          </div>
          <p
            v-for="issue in details.revision.qualityIssues.filter((item) => item.slideId === selectedSlide?.id)"
            :key="issue.code + issue.message"
            class="quality-warning"
          >
            <strong>Стоит проверить:</strong> {{ issue.message }}
          </p>
        </section>

        <aside v-if="selectedSlide" class="edit-panel">
          <div v-if="settingsOpen" class="deck-settings">
            <div class="panel-heading">
              <div>
                <span class="eyebrow">Вся презентация</span>
                <h2>Тема и формат</h2>
              </div>
              <button type="button" class="icon-button" aria-label="Закрыть" @click="settingsOpen = false">×</button>
            </div>

            <div class="field">
              <label for="deck-aspect">Соотношение сторон</label>
              <select id="deck-aspect" v-model="aspectChoice" :disabled="Boolean(busy)" @change="changeAspect">
                <option value="16:9">16:9 — экран и проектор</option>
                <option value="4:3">4:3 — классика</option>
                <option value="9:16">9:16 — телефон</option>
              </select>
            </div>

            <label class="field-label">Оформление</label>
            <ThemeGallery v-model="themeChoice" include-auto />
            <button type="button" class="button button--secondary button--wide" :disabled="Boolean(busy)" @click="changeTheme">
              <span v-if="busy === 'theme'" class="mini-loader"></span>
              Применить тему
            </button>
          </div>

          <template v-else>
            <div class="panel-heading">
              <div>
                <span class="eyebrow">Слайд {{ selectedIndex + 1 }}</span>
                <h2>Содержание</h2>
              </div>
              <span v-if="busy" class="save-indicator"><span class="mini-loader"></span></span>
            </div>

            <div class="field">
              <label for="slide-layout">Макет</label>
              <select id="slide-layout" v-model="selectedLayout" :disabled="Boolean(busy)">
                <option v-for="layout in layoutOptions" :key="layout.id" :value="layout.id">
                  {{ layout.name }} — {{ layout.hint }}
                </option>
              </select>
            </div>

            <div class="ai-rewrite">
              <label for="ai-instruction">Переделать только этот слайд</label>
              <div class="ai-rewrite__box">
                <textarea
                  id="ai-instruction"
                  v-model="aiInstruction"
                  rows="3"
                  maxlength="2000"
                  placeholder="Например: сделай короче и добавь конкретную цифру"
                ></textarea>
                <button
                  type="button"
                  class="button button--dark"
                  :disabled="Boolean(busy) || !aiInstruction.trim()"
                  @click="aiEdit"
                >
                  <span v-if="busy === 'ai'" class="mini-loader"></span>
                  Переделать
                </button>
              </div>
            </div>

            <div class="image-upload">
              <div>
                <strong>Своя картинка</strong>
                <small>PNG, JPG или WebP до 15 МБ</small>
              </div>
              <button type="button" class="button button--ghost" :disabled="Boolean(busy)" @click="uploadInput?.click()">
                {{ selectedSlide.image?.source === 'upload' ? 'Заменить' : 'Загрузить' }}
              </button>
              <input
                ref="uploadInput"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                @change="uploadImage"
              >
            </div>

            <SlideForm :slide="selectedSlide" :busy="busy === 'save'" @save="saveSlide" />
          </template>
        </aside>
      </div>
    </div>
  </AppShell>
</template>
